import { Modal } from '~/components/ui/Modal';
import { X, AlertTriangle } from 'lucide-react';
import { useNavigate } from '@remix-run/react';
import { useTranslation } from '~/lib/hooks/useTranslation';

interface StorageExceededModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function StorageExceededModal({ isOpen, onClose }: StorageExceededModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleUpgrade = () => {
    navigate('/billing');
    onClose();
  };

  const handleGoToMyProjects = () => {
    navigate('/settings/project-settings');
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="rounded-lg bg-[var(--alpha-background-secondary)] p-0 w-[480px] max-w-[90vw] text-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--alpha-border-primary)]">
          <div className="flex items-center gap-3">
            {' '}
            {/* Increased gap for better spacing */}
            <AlertTriangle size={24} className="text-red-500" />
            <h2 className="text-lg font-medium text-[var(--alpha-text-primary)]">
              {t('modals.storage.title', 'Storage has Exceeded')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--alpha-text-tertiary)] hover:text-[var(--alpha-text-primary)] p-1 rounded-md hover:bg-[var(--alpha-background-overlay-light)] transition-colors"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 text-sm text-[var(--alpha-text-secondary)] leading-relaxed">
          {' '}
          {/* Added leading-relaxed */}
          <p>
            {t(
              'modals.storage.message',
              'Your storage has exceeded. You need to delete projects, or upgrade to a higher plan.',
            )}
          </p>
          <div className="flex justify-start space-x-3 pt-4 rounded-b-lg">
            <button
              onClick={handleUpgrade}
              className="px-4 py-2 text-sm font-medium text-[var(--alpha-text-primary)] bg-gradient-to-r from-[var(--alpha-primary-start)] to-[var(--alpha-primary-end)] rounded-md hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--alpha-primary-start)] focus:ring-offset-[var(--alpha-background-editor-primary)]"
            >
              {t('modals.storage.upgrade', 'Upgrade')}
            </button>
            <button
              onClick={handleGoToMyProjects}
              className="px-4 py-2 text-sm font-medium text-[var(--alpha-text-secondary)] bg-[var(--alpha-background-overlay-light)] rounded-md hover:bg-[var(--alpha-background-overlay-primary)] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--alpha-background-overlay-primary)] focus:ring-offset-[var(--alpha-border-darker)]"
            >
              {t('modals.storage.goToProjects', 'Go to My Projects')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
